import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Mail, Eye, MousePointerClick, AlertTriangle, ShieldOff, CheckCircle2,
  RefreshCw, AlertCircle, ExternalLink,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import AdminTopNav from "../components/AdminTopNav";
import { isAdmin, loginUrl, getAdminToken } from "../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_WINDOWS = [
  { value: 7, label: "7 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 90, label: "90 Tage" },
];

export default function AdminMailEngagement() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!isAdmin()) navigate(loginUrl());
    document.title = "Mail-Engagement · Alphalane Admin";
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [aggRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/mail-engagement?days=${days}`, {
          headers: { "x-admin-token": getAdminToken() },
        }),
        axios.get(`${API}/admin/resend-webhook-stats`, {
          headers: { "x-admin-token": getAdminToken() },
        }).catch(() => null),
      ]);
      setData(aggRes.data);
      setStats(statsRes?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-mail-engagement">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin · Mail-Engagement</span>
            </div>
          </Link>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Mail-Engagement</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Open- und Click-Raten pro Drip-Mail-ID. Daten kommen direkt von Resend via Webhook.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="mail-engagement-window">
              {TIME_WINDOWS.map((w) => (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => setDays(w.value)}
                  data-testid={`mail-engagement-window-${w.value}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-display font-semibold transition-all ${
                    days === w.value ? "al-grad-bg text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="mail-engagement-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-sm font-display font-semibold transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
            </button>
          </div>
        </div>

        {data && !data.webhook_configured && (
          <WebhookSetupCard />
        )}

        {data && data.webhook_configured && stats && (
          <WebhookDiagnosticsCard stats={stats} totals={data.totals} />
        )}

        {err && (
          <div className="al-card p-4 mb-6 border-red-200 bg-red-50 text-red-900 flex items-start gap-3" data-testid="mail-engagement-error">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Daten konnten nicht geladen werden</div>
              <div className="text-xs mt-1">{err}</div>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Totals KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <Kpi icon={<Mail />} label="Versendet" value={data.totals.sent} testid="kpi-sent" />
              <Kpi icon={<CheckCircle2 />} label="Zugestellt" value={data.totals.delivered} testid="kpi-delivered" />
              <Kpi icon={<Eye />} label="Geöffnet" value={data.totals.opened} suffix={` · ${data.totals.open_rate}%`} testid="kpi-opened" tone="violet" />
              <Kpi icon={<MousePointerClick />} label="Geklickt" value={data.totals.clicked} suffix={` · ${data.totals.click_rate}%`} testid="kpi-clicked" tone="blue" />
              <Kpi icon={<AlertTriangle />} label="Bounces" value={data.totals.bounced} suffix={` · ${data.totals.bounce_rate}%`} testid="kpi-bounced" tone={data.totals.bounce_rate > 3 ? "red" : "amber"} />
              <Kpi icon={<ShieldOff />} label="Spam-Reports" value={data.totals.complained} testid="kpi-complained" tone={data.totals.complained > 0 ? "red" : "slate"} />
            </div>

            {/* Per-Mail breakdown table */}
            <section className="al-card overflow-hidden" data-testid="mail-engagement-table">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-display font-bold text-lg">Pro Drip-Mail</h2>
                <span className="text-xs text-slate-500 font-mono-tab">{data.rows.length} Mails · letzte {days} Tage</span>
              </div>
              {data.rows.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500">
                  Noch keine Events erfasst.{" "}
                  {!data.webhook_configured && (
                    <span>Webhook bei Resend konfigurieren (siehe Box oben) — danach kommen die Daten automatisch.</span>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 text-[11px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-5 py-3 font-bold">Mail-ID</th>
                        <th className="text-right px-3 py-3 font-bold">Versendet</th>
                        <th className="text-right px-3 py-3 font-bold">Zugestellt</th>
                        <th className="text-right px-3 py-3 font-bold">Open %</th>
                        <th className="text-right px-3 py-3 font-bold">Click %</th>
                        <th className="text-right px-3 py-3 font-bold">Bounce %</th>
                        <th className="text-right px-3 py-3 font-bold">Spam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.rows.map((r, i) => (
                        <tr key={`${r.mail_id}-${i}`} className="hover:bg-violet-50/30" data-testid={`mail-row-${r.mail_id || "null"}`}>
                          <td className="px-5 py-3">
                            <div className="font-mono-tab text-[13px] font-semibold text-slate-900">{r.mail_id || <span className="text-slate-400 italic">untagged</span>}</div>
                            {r.subject && <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-md">{r.subject}</div>}
                          </td>
                          <td className="text-right px-3 py-3 font-mono-tab text-slate-700">{r.sent}</td>
                          <td className="text-right px-3 py-3 font-mono-tab text-slate-700">{r.delivered}</td>
                          <td className="text-right px-3 py-3 font-mono-tab">
                            <RatePill value={r.open_rate} good={25} ok={15} />
                          </td>
                          <td className="text-right px-3 py-3 font-mono-tab">
                            <RatePill value={r.click_rate} good={5} ok={2} />
                          </td>
                          <td className="text-right px-3 py-3 font-mono-tab">
                            <RatePill value={r.bounce_rate} good={1} ok={3} inverse />
                          </td>
                          <td className="text-right px-3 py-3 font-mono-tab text-slate-700">
                            {r.complained > 0 ? (
                              <span className="text-red-600 font-bold">{r.complained}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────
function Kpi({ icon, label, value, suffix, testid, tone = "slate" }) {
  const toneClasses = {
    slate: "text-slate-700 bg-slate-50",
    violet: "text-violet-700 bg-violet-50",
    blue: "text-blue-700 bg-blue-50",
    amber: "text-amber-700 bg-amber-50",
    red: "text-red-700 bg-red-50",
  }[tone];
  return (
    <div className="al-card p-4" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-2 ${toneClasses}`}>
        {icon}
      </div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="font-display font-bold text-2xl text-slate-900 mt-1 font-mono-tab">
        {value.toLocaleString("de-CH")}{suffix && <span className="text-base font-semibold text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

function RatePill({ value, good, ok, inverse }) {
  // `inverse=true` → lower is better (used for bounce rate)
  let tone = "slate";
  if (inverse) {
    if (value <= good) tone = "green";
    else if (value <= ok) tone = "amber";
    else tone = "red";
  } else {
    if (value >= good) tone = "green";
    else if (value >= ok) tone = "amber";
    else tone = "slate";
  }
  const cls = {
    slate: "text-slate-500",
    green: "text-emerald-600 font-bold",
    amber: "text-amber-600 font-semibold",
    red: "text-red-600 font-bold",
  }[tone];
  return <span className={cls}>{value}%</span>;
}

function WebhookSetupCard() {
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/resend/webhook`;
  return (
    <div className="al-card p-5 mb-6 border-amber-200 bg-amber-50" data-testid="webhook-setup-card">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-display font-bold text-amber-900">Webhook noch nicht konfiguriert</h3>
          <p className="text-sm text-amber-900 mt-1 leading-relaxed">
            Damit hier Daten auftauchen, musst du Resend einmalig sagen, wohin es Events schicken soll. Dauert 2 Min:
          </p>
          <ol className="mt-3 space-y-1.5 text-sm text-amber-900 list-decimal pl-5">
            <li>Im Resend-Dashboard auf <strong>Webhooks → Add Webhook</strong></li>
            <li>URL eintragen:{" "}
              <code className="bg-white border border-amber-200 px-2 py-0.5 rounded text-[12px] font-mono select-all">
                {webhookUrl}
              </code>
            </li>
            <li>Events aktivieren: <code className="font-mono text-[12px]">email.sent</code>, <code className="font-mono text-[12px]">email.delivered</code>, <code className="font-mono text-[12px]">email.opened</code>, <code className="font-mono text-[12px]">email.clicked</code>, <code className="font-mono text-[12px]">email.bounced</code>, <code className="font-mono text-[12px]">email.complained</code></li>
            <li><strong>Signing Secret</strong> kopieren und mir geben — kommt in die <code className="font-mono text-[12px]">RESEND_WEBHOOK_SECRET</code> env var</li>
          </ol>
          <a
            href="https://resend.com/webhooks"
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-sm font-display font-semibold text-amber-900 hover:text-amber-700"
          >
            Resend Webhooks öffnen <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function WebhookDiagnosticsCard({ stats, totals }) {
  const { diagnosis, hint, total_7d, total_24h, by_outcome, recent, webhook_url, last_hit } = stats || {};
  const isOk = diagnosis === "ok";
  // No need to render anything if everything is fine AND we have delivered events.
  if (isOk && totals && totals.delivered > 0) return null;

  const toneClass = isOk
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : diagnosis === "no_hits"
    ? "border-red-200 bg-red-50 text-red-900"
    : "border-amber-200 bg-amber-50 text-amber-900";

  const TitleIcon = isOk ? CheckCircle2 : AlertCircle;
  const titleLabel = {
    no_hits: "Keine Webhook-Events angekommen",
    signature_mismatch: "Signing Secret stimmt nicht überein",
    missing_tags: "Events ohne unsere Tags",
    no_match: "Events kommen an, aber Mapping schlägt fehl",
    ok: "Webhook läuft sauber",
  }[diagnosis || "no_hits"];

  return (
    <div className={`al-card p-5 mb-6 ${toneClass}`} data-testid="webhook-diagnostics-card">
      <div className="flex items-start gap-3">
        <TitleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-display font-bold">{titleLabel}</h3>
            <div className="text-xs font-mono-tab">
              <span className="font-semibold">{total_24h}</span> Hits / 24h ·{" "}
              <span className="font-semibold">{total_7d}</span> / 7d
            </div>
          </div>
          <p className="text-sm mt-1 leading-relaxed">{hint}</p>

          <div className="mt-3 text-xs">
            <div className="font-bold uppercase tracking-widest mb-1.5">Webhook-URL</div>
            <code className="bg-white/60 border border-current/20 px-2 py-1 rounded select-all">{webhook_url}</code>
          </div>

          {Object.keys(by_outcome || {}).length > 0 && (
            <div className="mt-3 text-xs">
              <div className="font-bold uppercase tracking-widest mb-1.5">Hit-Outcomes (7d)</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(by_outcome).map(([k, v]) => (
                  <div key={k} className="bg-white/60 border border-current/20 px-2.5 py-1 rounded-full font-mono-tab" data-testid={`outcome-${k}`}>
                    <span className="font-bold">{k}</span>: {v.count}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recent && recent.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer font-bold uppercase tracking-widest">Letzte {recent.length} Hits anzeigen</summary>
              <div className="mt-2 max-h-64 overflow-y-auto border border-current/20 rounded-lg bg-white/60 divide-y divide-current/10">
                {recent.map((r, i) => (
                  <div key={`${r.received_at || i}-${r.event_type || ""}`} className="px-3 py-2 font-mono-tab text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold">{r.outcome}</span>
                      <span className="opacity-60">{r.received_at?.slice(0, 19).replace("T", " ")}</span>
                    </div>
                    {(r.event_type || r.mail_id) && (
                      <div className="opacity-70 mt-0.5">
                        {r.event_type && <span>{r.event_type}</span>}{" "}
                        {r.mail_id && <span>· mail_id={r.mail_id}</span>}{" "}
                        {r.collection && <span>· {r.collection}</span>}{" "}
                        {r.matched === false && <span className="text-red-700 font-bold">· NO MATCH</span>}
                      </div>
                    )}
                    {r.error && <div className="text-red-700 mt-0.5 truncate">⚠️ {r.error}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}

          {!isOk && (
            <div className="mt-3 text-xs space-y-1.5">
              <div className="font-bold uppercase tracking-widest">Was tun?</div>
              {diagnosis === "no_hits" && (
                <ul className="list-disc pl-5 space-y-1">
                  <li>Im Resend-Dashboard prüfen, ob die Webhook-URL exakt obenstehende URL ist</li>
                  <li>Events aktivieren: <code>email.sent</code>, <code>email.delivered</code>, <code>email.opened</code>, <code>email.clicked</code></li>
                  <li>Testmail über &bdquo;Mail-Test&ldquo;-Tab schicken → Hit muss innerhalb 30 Sek hier auftauchen</li>
                </ul>
              )}
              {diagnosis === "signature_mismatch" && (
                <ul className="list-disc pl-5 space-y-1">
                  <li>Im Resend-Dashboard das Signing-Secret kopieren (beginnt mit <code>whsec_</code>)</li>
                  <li>Backend-Env aktualisieren: <code>RESEND_WEBHOOK_SECRET=whsec_xxx</code></li>
                  <li>Backend neustarten (auf Production: re-deploy)</li>
                </ul>
              )}
              {diagnosis === "missing_tags" && (
                <ul className="list-disc pl-5 space-y-1">
                  <li>Vermutlich werden Mails versendet, die nicht über drip_engine laufen</li>
                  <li>Check, welche Funktionen direkt <code>resend.Emails.send</code> aufrufen → dort ggf. Tags ergänzen</li>
                </ul>
              )}
              {diagnosis === "no_match" && (
                <ul className="list-disc pl-5 space-y-1">
                  <li>Customer-Merges können die contact_id ändern</li>
                  <li>Letzte Hits oben durchsehen, welche mail_id+contact_id-Kombi nicht matched</li>
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

